import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    AuthTokenPair,
    clearTokensFromStorage,
    getCurrentUser,
    getTokensFromStorage,
    setTokensToStorage,
} from '@round/api';
import { datadogRum } from '@datadog/browser-rum';
import type { ImpersonationSession } from '@/auth/impersonation';
import {
    clearImpersonationSession,
    getImpersonationSession,
    setImpersonationSession,
    takeImpersonationTokenFromUrl,
} from '@/auth/impersonation';

// Thrown by @round/api refreshTokens() when the refresh token itself is
// rejected. There is no recovering the session at that point.
const REFRESH_FAILURE_MESSAGE = 'Could not refresh tokens';

type WaveAuthContextValue = {
    isAuthenticated: boolean;
    isRestoring: boolean;
    impersonation: ImpersonationSession | null;
    setTokens: (tokens: AuthTokenPair) => void;
    logout: () => void;
};

/**
 * A staff impersonation link hands over a lone access token. Any tokens already in
 * storage are dropped first: leaving a previous refresh token behind would let the
 * session outlive the 15-minute impersonation token, which is the whole point of it.
 */
const startImpersonationFromUrl = (): string | null => {
    const handoff = takeImpersonationTokenFromUrl();
    if (handoff === null) {
        return null;
    }

    clearTokensFromStorage();
    if (import.meta.env.PROD) {
        // Whoever was signed in here is being replaced; without this their id stays on
        // the RUM session and the admin's clicks are attributed to them.
        datadogRum.clearUser();
    }
    setTokensToStorage({ access: handoff.token, refresh: null });
    setImpersonationSession(handoff.session);
    return handoff.token;
};

const WaveAuthContext = createContext<WaveAuthContextValue | null>(null);

/**
 * Light portal auth context. Persists JWTs via the shared @round/api storage
 * helpers so call() auto-attaches Authorization: JWT <access>, and tracks
 * authenticated state off the access token's presence.
 */
export const WaveAuthProvider = ({ children }: { children: ReactNode }) => {
    const queryClient = useQueryClient();
    const [accessToken, setAccessToken] = useState<string | null>(
        () => startImpersonationFromUrl() ?? getTokensFromStorage().access
    );
    const [impersonation, setImpersonation] = useState<ImpersonationSession | null>(getImpersonationSession);
    const [isRestoring, setIsRestoring] = useState(true);

    // Token restoration itself is synchronous, but keep the first routing pass
    // behind an explicit ready state. Public `/c` and `/i` routes can then avoid
    // rendering guest-only content during application hydration.
    useEffect(() => {
        setIsRestoring(false);
    }, []);

    // A normal token pair is by definition not an impersonation, so signing in over the
    // top of one drops the marker. Otherwise a real creator logging in on the same
    // browser would keep seeing the banner.
    const setTokens = useCallback((tokens: AuthTokenPair) => {
        clearImpersonationSession();
        setImpersonation(null);
        setTokensToStorage(tokens);
        setAccessToken(tokens.access);
    }, []);

    const logout = useCallback(() => {
        clearTokensFromStorage();
        clearImpersonationSession();
        setAccessToken(null);
        setImpersonation(null);
        queryClient.clear();
        if (import.meta.env.PROD) {
            datadogRum.clearUser();
        }
    }, [queryClient]);

    // Tag the authenticated creator so a single cross-app RUM dashboard can facet
    // DAU/MAU by user type (ROU-1191). Keyed on the shared round user id, matching
    // how the round app tags its own users. An impersonated session is a Wave admin
    // looking around, so it is left out of the creator activity numbers.
    useEffect(() => {
        if (!import.meta.env.PROD || accessToken === null || impersonation !== null) {
            return;
        }

        let isActive = true;
        getCurrentUser()
            .then((user) => {
                if (isActive) {
                    datadogRum.setUser({ id: user.id.toString(), role: 'wave_creator' });
                }
            })
            .catch(() => {
                // Best-effort tagging; a failed lookup must not affect the session.
            });

        return () => {
            isActive = false;
        };
    }, [accessToken, impersonation]);

    // A fully expired session (refresh token rejected) would otherwise leave the
    // user "authenticated" with every query stuck in an error state and no way
    // back to login. Watch for the refresh failure and drop the session so
    // routing falls back to the unauthenticated flow.
    useEffect(() => {
        return queryClient.getQueryCache().subscribe((event) => {
            if (
                event.type === 'updated' &&
                event.action.type === 'error' &&
                event.action.error instanceof Error &&
                event.action.error.message === REFRESH_FAILURE_MESSAGE
            ) {
                logout();
            }
        });
    }, [queryClient, logout]);

    const value = useMemo<WaveAuthContextValue>(
        () => ({ isAuthenticated: accessToken !== null, isRestoring, impersonation, setTokens, logout }),
        [accessToken, isRestoring, impersonation, setTokens, logout]
    );

    return <WaveAuthContext.Provider value={value}>{children}</WaveAuthContext.Provider>;
};

export const useWaveAuth = (): WaveAuthContextValue => {
    const context = useContext(WaveAuthContext);
    if (context === null) {
        throw new Error('useWaveAuth must be used within a WaveAuthProvider');
    }
    return context;
};
